// src/pages/QuizPage/components/QuestionBody.tsx
import { useState, useEffect, useMemo } from "react";
import { Flag, Sparkles, Loader2, X, BookOpen, AlertTriangle } from "lucide-react";
import type { Question } from "../utils/quizTypes";
import { shuffleArray, getShuffleSettings } from "../utils/shuffleUtils";
import ImageZoomModal from "../../../components/ImageZoomModal";
import ReportQuestionModal from "../../../components/ReportQuestionModal";
import { aiUstozAPI, userAnswersAPI } from "../../../api";
const IMG_BASE = process.env.REACT_APP_BASE_URL_IMAGE;
const DEFAULT_IMG = "https://avtomind.uz/storage/photos/default.webp";

// AI izohidagi **muhim** qismlarni kitobdagi sariq flomaster kabi
// ajratib ko'rsatish uchun — to'liq markdown kutubxonasiz, faqat shu
// bitta pattern (**...**) kerak bo'lgani uchun.
function renderHighlighted(text: string) {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith("**") && part.endsWith("**")) {
      return (
        <mark
          key={i}
          style={{
            background: "rgba(250, 204, 21, 0.28)",
            color: "#fef9c3",
            padding: "0 3px",
            borderRadius: 3,
          }}>
          {part.slice(2, -2)}
        </mark>
      );
    }
    return <span key={i}>{part}</span>;
  });
}

interface QuestionBodyProps {
  question: Question;
  current: number;
  answered: Record<number, number>;
  onAnswer: (orderIndex: number) => void;
  displayChoices?: Array<{ id: number; order_index: number; text: string }>;
  lang?: "oz" | "uz" | "ru";
}

export default function QuestionBody({
  question,
  current,
  answered,
  onAnswer,
  displayChoices: propDisplayChoices,
  lang = "uz",
}: QuestionBodyProps) {
  const [isImageOpen, setIsImageOpen] = useState(false);
  const [isReportOpen, setIsReportOpen] = useState(false);
  // loadedSrc: qaysi src yuklanganini saqlaymiz
  // imgLoaded: render vaqtida hisoblanadi — race condition yo'q
  const [loadedSrc, setLoadedSrc] = useState<string | null>(null);

  const hasImage = !!question.photo_url;
  const imgSrc = hasImage ? `${IMG_BASE}/${question.photo_url}` : DEFAULT_IMG;
  const imgLoaded = loadedSrc === imgSrc;

  // AI izoh — savol o'zgarganda tozalanadi, faqat foydalanuvchi bosganda
  // yuklanadi (avtomatik emas — ortiqcha so'rov yubormaslik uchun)
  const [aiExplanation, setAiExplanation] = useState<string | null>(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);
  // Qaysi izoh oynasi ochiq: "db" (statik izoh) | "ai" (AI izoh) | "mistake" (shaxsiy xato soni) | null
  const [openExplanation, setOpenExplanation] = useState<
    "db" | "ai" | "mistake" | null
  >(null);

  // Shaxsiy xato soni — faqat javob NOTO'G'RI bo'lganda so'raladi.
  // Backend hali joriy (tugallanmagan) sessiyani bilmaydi, shuning uchun
  // +1 qilib ko'rsatamiz (hozirgi xato ham hisobga qo'shiladi).
  const [myMistakeCount, setMyMistakeCount] = useState<number | null>(null);
  const isWrong =
    answered[current] !== undefined &&
    answered[current] !== question.correct_answer_index;

  useEffect(() => {
    setAiExplanation(null);
    setAiLoading(false);
    setAiError(null);
    setOpenExplanation(null);
    setMyMistakeCount(null);
  }, [question.id]);

  useEffect(() => {
    if (!isWrong) return;
    let cancelled = false;
    userAnswersAPI
      .getMyMistakeCountForQuestion(question.id)
      .then((res) => {
        if (!cancelled) setMyMistakeCount((res.data?.count ?? 0) + 1);
      })
      .catch(() => {
        if (!cancelled) setMyMistakeCount(1);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [question.id, isWrong]);

  const handleLoadAiExplanation = async () => {
    setOpenExplanation("ai");
    if (aiExplanation || aiLoading) return;
    setAiLoading(true);
    setAiError(null);
    try {
      const res = await aiUstozAPI.explainQuestion(question.id, lang);
      setAiExplanation(res.data?.explanation || "");
    } catch {
      setAiError("AI izohni yuklab bo'lmadi, qaytadan urinib ko'ring");
    } finally {
      setAiLoading(false);
    }
  };

  // Javoblar aralash — question.id o'zgarganda qayta shuffle, re-renderlarda stable
  // propDisplayChoices berilsa (masalan Duel/review mode) — shu ishlatiladi
  const displayChoices = useMemo(() => {
    if (propDisplayChoices) return propDisplayChoices;
    const { shuffleAnswers } = getShuffleSettings();
    return shuffleAnswers ? shuffleArray(question.choices) : question.choices;
  }, [question.id, propDisplayChoices]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setIsImageOpen(false);
    // 8s timeout: server javob bermasa ham spinner chiqmasin
    const timer = setTimeout(() => setLoadedSrc(imgSrc), 8000);
    return () => clearTimeout(timer);
  }, [imgSrc]);

  const hasAnswered = answered[current] !== undefined;

  return (
    <>
      {/* Main body */}
      <div className="w-full max-w-7xl mx-auto px-4 py-4 flex flex-col gap-4">
        {/* Question title */}
        <div className="flex items-start justify-between gap-3">
          <h1
            className="font-semibold text-white/95 leading-relaxed"
            style={{ fontSize: "calc(1.375rem * var(--quiz-scale, 1))" }}>
            {question.question}
          </h1>
          <button
            onClick={() => setIsReportOpen(true)}
            title="Shikoyat"
            className="shrink-0 w-9 h-9 rounded-xl flex items-center justify-center text-white/40 hover:text-red-400 hover:bg-red-500/10 transition-all">
            <Flag size={16} />
          </button>
        </div>

        {/* Layout:
            Mobile  → flex-col: rasm (agar mavjud) tepada, javoblar pastda
            Desktop → grid: javoblar chapda, rasm o'ngda (eski holatdek) */}
        <div className="flex flex-col lg:grid lg:grid-cols-[1fr_minmax(320px,480px)] gap-4 lg:gap-6 items-start">
          {/* Image:
              Mobile  → faqat photo_url bo'lsa ko'rinadi (order-first = tepada)
              Desktop → har doim ko'rinadi, default rasm bilan (order-last = o'ngda) */}
          <div
            className={`order-first lg:order-last w-full rounded-2xl overflow-hidden border border-white/10 bg-black/30 lg:sticky lg:top-4 relative h-[200px] lg:h-[min(420px,48vh)] ${
              !hasImage ? "hidden lg:block" : ""
            }`}>
            {!imgLoaded && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                <div className="w-8 h-8 rounded-full border-2 border-blue-500/20 border-t-blue-500 animate-spin" />
              </div>
            )}
            <img
              key={imgSrc}
              src={imgSrc}
              alt="Quiz"
              className={`w-full h-full object-contain bg-black transition-opacity duration-300 ${
                imgLoaded ? "opacity-100" : "opacity-0"
              } ${hasImage ? "cursor-zoom-in hover:scale-[1.01]" : "cursor-default"}`}
              onClick={() => hasImage && setIsImageOpen(true)}
              onLoad={() => setLoadedSrc(imgSrc)}
              onError={(e) => {
                // DEFAULT_IMG ham yuklanmasa ikkinchi marta o'rnatma (cheksiz loop)
                if (e.currentTarget.src !== DEFAULT_IMG) {
                  e.currentTarget.src = DEFAULT_IMG;
                }
                setLoadedSrc(imgSrc);
              }}
            />
          </div>

          {/* Answers — mobileda pastda, desktopda chapda */}
          <div className="order-last lg:order-first w-full flex flex-col gap-3">
            {displayChoices.map((choice, idx) => {
              const isSelected = answered[current] === choice.order_index;
              const isCorrect =
                choice.order_index === question.correct_answer_index;

              let bg = "bg-white/[0.04] border-white/10 text-white/80";
              let icon = "border-white/20 text-white/40";

              if (hasAnswered) {
                if (isCorrect) {
                  bg = "bg-green-500/15 border-green-500/40 text-green-200";
                  icon = "border-green-400 text-green-300 bg-green-500/20";
                } else if (isSelected) {
                  bg = "bg-red-500/15 border-red-500/40 text-red-200";
                  icon = "border-red-400 text-red-300 bg-red-500/20";
                } else {
                  bg = "opacity-40 border-white/10 text-white/50";
                }
              }

              return (
                <button
                  key={choice.id}
                  disabled={hasAnswered}
                  onClick={() => onAnswer(choice.order_index)}
                  className={`w-full p-3 md:p-4 rounded-2xl border text-left flex items-center gap-3 md:gap-4 transition-all duration-150 ${bg} ${
                    !hasAnswered ?
                      "hover:border-blue-500/50 hover:bg-blue-500/5 active:scale-[0.99]"
                    : ""
                  }`}>
                  <div
                    className={`w-10 h-10 md:w-11 md:h-11 shrink-0 rounded-xl border-2 flex items-center justify-center font-black text-sm transition-all ${icon}`}>
                    F{idx + 1}
                  </div>
                  <span
                    className="leading-snug"
                    style={{ fontSize: "calc(1.0625rem * var(--quiz-scale, 1))" }}>
                    {choice.text}
                  </span>
                </button>
              );
            })}

            {/* Shaxsiy xato soni — faqat 2-marta va undan ko'p xato qilinganda */}
            {isWrong && myMistakeCount !== null && myMistakeCount >= 2 && (
              <button
                onClick={() => setOpenExplanation("mistake")}
                className="mt-2 w-full flex items-center justify-center gap-2 rounded-2xl border border-orange-500/30 bg-orange-500/[0.06] py-2.5 text-sm font-semibold text-orange-300 hover:bg-orange-500/10 active:scale-[0.99] transition-all">
                <AlertTriangle size={15} />
                Siz bu savolni {myMistakeCount} marta xato qildingiz
              </button>
            )}

            {/* Izoh tugmalari — faqat javob berilgandan keyin, yonma-yon */}
            {hasAnswered && (
              <div className="mt-2 grid grid-cols-2 gap-3">
                {question.explanation && (
                  <button
                    onClick={() => setOpenExplanation("db")}
                    className="flex items-center justify-center gap-2 rounded-2xl border border-white/10 bg-white/[0.04] py-3 text-sm font-semibold text-white/80 hover:bg-white/[0.07] active:scale-[0.99] transition-all">
                    <BookOpen size={16} />
                    Izoh
                  </button>
                )}
                <button
                  onClick={handleLoadAiExplanation}
                  className={`flex items-center justify-center gap-2 rounded-2xl border border-blue-500/30 bg-blue-500/[0.06] py-3 text-sm font-semibold text-blue-300 hover:bg-blue-500/10 active:scale-[0.99] transition-all ${
                    !question.explanation ? "col-span-2" : ""
                  }`}>
                  <Sparkles size={16} />
                  AI izoh
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Image zoom modal */}
      {isImageOpen && hasImage && (
        <ImageZoomModal src={imgSrc} onClose={() => setIsImageOpen(false)} />
      )}

      {/* Report question modal */}
      {isReportOpen && (
        <ReportQuestionModal
          questionId={question.id}
          onClose={() => setIsReportOpen(false)}
        />
      )}

      {/* Izoh modal — "Izoh" va "AI izoh" ikkalasi ham shu orqali ochiladi */}
      {openExplanation && (
        <div
          className="fixed inset-0 z-[200] flex items-center justify-center p-4"
          style={{ background: "rgba(0,0,0,.7)" }}
          onClick={() => setOpenExplanation(null)}>
          <div
            className="w-full max-w-md max-h-[75vh] flex flex-col rounded-2xl p-5"
            style={{ background: "var(--card)", border: "1px solid var(--border)" }}
            onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4 shrink-0">
              <div className="flex items-center gap-2 font-bold text-base">
                {openExplanation === "ai" ?
                  <>
                    <Sparkles size={16} className="text-blue-400" />
                    <span className="text-blue-300">AI izoh</span>
                  </>
                : openExplanation === "mistake" ?
                  <>
                    <AlertTriangle size={16} className="text-orange-400" />
                    <span className="text-orange-300">Xatolar statistikasi</span>
                  </>
                : <>
                    <BookOpen size={16} className="text-white/70" />
                    <span className="text-white/90">Izoh</span>
                  </>
                }
              </div>
              <button
                onClick={() => setOpenExplanation(null)}
                className="text-gray-500 hover:text-white transition-colors">
                <X size={18} />
              </button>
            </div>

            <div
              className="overflow-y-auto select-none"
              style={{ WebkitUserSelect: "none", userSelect: "none" }}
              onCopy={(e) => e.preventDefault()}
              onContextMenu={(e) => e.preventDefault()}>
              {openExplanation === "db" && (
                <p className="text-sm text-white/70 leading-relaxed">
                  {question.explanation}
                </p>
              )}

              {openExplanation === "ai" && (
                <>
                  {aiLoading && (
                    <div className="flex items-center justify-center gap-2 py-8 text-sm text-white/50">
                      <Loader2 size={18} className="animate-spin" />
                      AI izoh tayyorlanmoqda...
                    </div>
                  )}
                  {aiError && (
                    <p className="text-sm text-red-300">{aiError}</p>
                  )}
                  {aiExplanation && (
                    <p className="text-sm text-white/80 leading-relaxed whitespace-pre-line">
                      {renderHighlighted(aiExplanation)}
                    </p>
                  )}
                </>
              )}

              {openExplanation === "mistake" && (
                <p className="text-sm text-white/80 leading-relaxed">
                  Siz ushbu savolga hozirgacha{" "}
                  <span className="font-bold text-orange-300">
                    {myMistakeCount}
                  </span>{" "}
                  marta xato javob bergansiz. Bu savolni "Izoh" va "AI izoh"
                  bo'limlari orqali qayta o'rganib chiqishni tavsiya qilamiz —
                  shunda xatoni takrorlamaysiz.
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
